import { SearchOutlined } from '@mui/icons-material';
import {
    Autocomplete,
    FormControl,
    InputAdornment,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    TextField,
} from '@mui/material';

import useTranslate from 'hooks/useTranslate';
import type { AdminConsultantRole, ServiceProviderAdminRow } from 'types';
import {
    ADMIN_CONSULTANT_ROLES,
    getAdminConsultantRoleLabelKey,
} from 'views/v2/adminPanel/helpers/adminConsultants';

interface ConsultantsFiltersProps {
    search: string;
    onSearchChange: (value: string) => void;
    selectedRole: AdminConsultantRole | '';
    onRoleChange: (role: AdminConsultantRole | '') => void;
    providers: ServiceProviderAdminRow[];
    selectedProviderId: number | null;
    onProviderChange: (providerId: number | null) => void;
}

const fieldSx = { width: { xs: '100%', sm: 220 }, maxWidth: '100%' };

export function ConsultantsFilters({
    search,
    onSearchChange,
    selectedRole,
    onRoleChange,
    providers,
    selectedProviderId,
    onProviderChange,
}: ConsultantsFiltersProps) {
    const { translate } = useTranslate();
    const selectedProvider =
        providers.find((provider) => provider.id === selectedProviderId) ?? null;

    return (
        <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={1.5}
            sx={{ alignItems: 'flex-start' }}
        >
            <TextField
                size="small"
                label={translate('searchConsultants')}
                placeholder={translate('nameEmailOrLicense')}
                value={search}
                onChange={(event) => onSearchChange(event.target.value)}
                sx={{ ...fieldSx, width: { xs: '100%', sm: 280 } }}
                slotProps={{
                    input: {
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchOutlined sx={{ fontSize: 18 }} />
                            </InputAdornment>
                        ),
                    },
                }}
            />
            <FormControl size="small" sx={fieldSx}>
                <InputLabel id="consultant-role-filter-label">{translate('role')}</InputLabel>
                <Select
                    labelId="consultant-role-filter-label"
                    label={translate('role')}
                    value={selectedRole}
                    onChange={(event) =>
                        onRoleChange(event.target.value as AdminConsultantRole | '')
                    }
                >
                    <MenuItem value="">{translate('allRoles')}</MenuItem>
                    {ADMIN_CONSULTANT_ROLES.map((role) => (
                        <MenuItem key={role} value={role}>
                            {translate(getAdminConsultantRoleLabelKey(role))}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
            <Autocomplete
                size="small"
                options={providers}
                value={selectedProvider}
                onChange={(_event, provider) => onProviderChange(provider?.id ?? null)}
                getOptionLabel={(provider) => provider.company_name}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                sx={fieldSx}
                renderInput={(params) => (
                    <TextField {...params} label={translate('serviceProvider')} />
                )}
            />
        </Stack>
    );
}

export default ConsultantsFilters;
